import React, { useState, useCallback, useRef, useEffect } from "react";
import { Card, Row, Col, Form, Button, Spinner, InputGroup, Container, ListGroup, Accordion, Badge } from "react-bootstrap";
import AddPhotoAlternateIcon from "@material-ui/icons/AddPhotoAlternate";
import VisibilityIcon from "@material-ui/icons/Visibility";
import BusinessIcon from "@material-ui/icons/Business";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import InfoIcon from "@material-ui/icons/Info";
import PeopleIcon from "@material-ui/icons/People";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import usePlacesAutocomplete, { getGeocode, getLatLng } from "use-places-autocomplete";
import { firebaseConfig } from "../services/firebase/Config";

const mapContainerStyle = {
  width: '100%',
  height: '300px',
  borderRadius: '8px',
  marginTop: '10px'
};

const defaultCenter = {
  lat: -12.046374,
  lng: -77.042793
};

const libraries = ["places"];

function AddressSearch({ onAddressSelect, initialAddress, initialCoords }) {
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      location: { lat: () => -12.046374, lng: () => -77.042793 },
      radius: 200 * 1000,
    },
    debounce: 300,
  });

  useEffect(() => {
    if (initialAddress) {
      setValue(initialAddress, false);
    }
  }, [initialAddress]);

  const handleInput = (e) => {
    setValue(e.target.value);
  };

  const handleSelect = async (address) => {
    setValue(address, false);
    clearSuggestions();

    try {
      const results = await getGeocode({ address });
      const { lat, lng } = await getLatLng(results[0]);
      onAddressSelect({ address, lat, lng });
    } catch (error) {
      console.error("Error setting address: ", error);
    }
  };

  return (
    <div className="position-relative">
      <Form.Control
        value={value}
        onChange={handleInput}
        disabled={!ready}
        placeholder="Busca la dirección de tu consultorio..."
        autoComplete="off"
      />
      {status === "OK" && (
        <ListGroup className="position-absolute w-100 shadow-sm" style={{ zIndex: 1000 }}>
          {data.map(({ place_id, description }) => (
            <ListGroup.Item
              key={place_id}
              action
              onClick={() => handleSelect(description)}
              className="py-2"
            >
              <i className="fas fa-map-marker-alt me-2 text-primary"></i>
              {description}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </div>
  );
}

const EspecialistaCard = ({ esp, onEdit }) => {
  const isOdontologo = ['ODONTOLOGO', 'ODONTOLOGO_ADM'].includes(esp.perfil);
  
  return (
    <Col md={6} lg={4} className="mb-3">
      <Card className="h-100 border-0 shadow-sm hover-card" style={{ borderRadius: '15px', background: '#fff' }}>
        <Card.Body className="p-3 d-flex flex-column align-items-center text-center">
          <div className="position-relative mb-2">
            {esp.foto ? (
              <img 
                src={esp.foto} 
                alt={esp.nombres} 
                style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #e7f1ff' }} 
              />
            ) : (
              <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PeopleIcon style={{ fontSize: '40px', color: '#cbd5e1' }} />
              </div>
            )}
            <Badge 
              pill 
              bg={isOdontologo ? 'primary' : 'secondary'} 
              className="position-absolute bottom-0 end-0 px-2 py-1"
              style={{ fontSize: '0.6rem' }}
            >
              {isOdontologo ? 'DOC' : 'ADM'}
            </Badge>
          </div>
          <h6 className="fw-bold mb-1 text-truncate w-100">{esp.nombres}</h6>
          <p className="text-muted small mb-2 text-truncate w-100">{esp.especialidad || esp.perfil}</p>
          <Button 
            variant="outline-primary" 
            size="sm" 
            className="mt-auto px-3 rounded-pill d-flex align-items-center gap-1 mx-auto"
            onClick={() => onEdit(esp)}
          >
            <EditIcon fontSize="small" /> Perfil
          </Button>
        </Card.Body>
      </Card>
    </Col>
  );
};

export default function EmpresaForm({
  empresa,
  loading,
  uploading,
  especialistas = [],
  onChange,
  onPhoneChange,
  onFileChange,
  onAddressChange,
  onSubmit,
  onPreview,
  onEditEspecialista,
  onNewEspecialista,
  showSubmit = true
}) {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: firebaseConfig.apiKey,
    libraries
  });

  const mapRef = useRef();
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const center = empresa.lat && empresa.lng
    ? { lat: parseFloat(empresa.lat), lng: parseFloat(empresa.lng) }
    : defaultCenter;

  if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" />
      </div>
    );

  return (
    <Container className="py-3">
      <Card className="shadow-lg border-0 mx-auto" style={{ maxWidth: 900, borderRadius: '20px', overflow: 'hidden' }}>
        <div className="bg-primary p-4 d-flex justify-content-between align-items-center text-white">
          <div>
            <h3 className="mb-0 fw-bold text-white">Configuración de Clínica</h3>
            <p className="mb-0 small opacity-75">Gestiona la información de tu consultorio y equipo</p>
          </div>
          {onPreview && (
            <Button 
                variant="light" 
                className="d-flex align-items-center gap-2 fw-bold text-primary shadow-sm"
                onClick={onPreview}
                style={{ borderRadius: '12px' }}
            >
              <VisibilityIcon fontSize="small" /> Ver Previa
            </Button>
          )}
        </div>
        
        <Card.Body className="p-4">
          <Form onSubmit={onSubmit}>
            <Accordion defaultActiveKey="0" className="custom-accordion">
              {/* SECCION 1: IDENTIDAD */}
              <Accordion.Item eventKey="0" className="border-0 mb-3 shadow-sm rounded">
                <Accordion.Header className="rounded">
                  <div className="d-flex align-items-center gap-2">
                    <BusinessIcon className="text-primary" />
                    <span className="fw-bold">Identidad y Presentación (Landing Page)</span>
                  </div>
                </Accordion.Header>
                <Accordion.Body className="bg-light rounded-bottom">
                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="fw-bold text-secondary">Razón Social</Form.Label>
                        <Form.Control
                          name="razonSocial"
                          placeholder="Nombre de tu clínica"
                          type="text"
                          value={empresa.razonSocial || ''}
                          onChange={onChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={8}>
                      <Form.Group>
                        <Form.Label className="fw-bold text-secondary">URL Amigable (Slug)</Form.Label>
                        <InputGroup>
                          <InputGroup.Text className="bg-white text-muted" style={{fontSize: '0.8rem'}}>pac.sysdent.net/</InputGroup.Text>
                          <Form.Control
                            name="slug"
                            placeholder="clinica-dental-ejemplo"
                            type="text"
                            value={empresa.slug || ''}
                            onChange={onChange}
                            required
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-bold text-secondary">Logo</Form.Label>
                        <div className="d-flex align-items-center gap-2">
                          <label htmlFor="logo-upload" className="btn btn-outline-primary btn-sm mb-0 w-100 py-2">
                            <AddPhotoAlternateIcon /> Subir
                          </label>
                          <input id="logo-upload" type="file" className="d-none" onChange={onFileChange} />
                          {empresa.logoUrl && !uploading && (
                            <img src={empresa.logoUrl} alt="Logo" style={{ height: 40, borderRadius: 4, border: "1px solid #ddd", background: "#fff" }} />
                          )}
                          {uploading && <Spinner animation="border" size="sm" />}
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-0">
                    <Form.Label className="fw-bold text-secondary">Descripción (Misión / Visión)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="descripcion"
                      placeholder="Cuéntanos sobre tu clínica para atraer a tus pacientes..."
                      value={empresa.descripcion || ''}
                      onChange={onChange}
                    />
                    <Form.Text className="text-muted small">Este texto es el corazón de tu página principal.</Form.Text>
                  </Form.Group>
                </Accordion.Body>
              </Accordion.Item>

              {/* SECCION 2: EQUIPO DE ESPECIALISTAS */}
              <Accordion.Item eventKey="3" className="border-0 mb-3 shadow-sm rounded">
                <Accordion.Header>
                  <div className="d-flex align-items-center gap-2">
                    <PeopleIcon className="text-primary" />
                    <span className="fw-bold">Equipo de Especialistas</span>
                  </div>
                </Accordion.Header>
                <Accordion.Body className="bg-light rounded-bottom">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <p className="text-muted mb-0 small">Los especialistas con perfil público aparecerán en tu landing page.</p>
                    <Button variant="primary" size="sm" className="rounded-pill d-flex align-items-center gap-1" onClick={onNewEspecialista}>
                      <AddIcon fontSize="small" /> Nuevo
                    </Button>
                  </div>
                  <Row>
                    {especialistas.length > 0 ? (
                        especialistas.map(esp => (
                            <EspecialistaCard key={esp.id} esp={esp} onEdit={onEditEspecialista} />
                        ))
                    ) : (
                        <Col className="text-center py-4">
                            <p className="text-muted">No se encontraron especialistas.</p>
                        </Col>
                    )}
                  </Row>
                </Accordion.Body>
              </Accordion.Item>

              {/* SECCION 3: CONTACTO Y UBICACION */}
              <Accordion.Item eventKey="1" className="border-0 mb-3 shadow-sm rounded">
                <Accordion.Header>
                  <div className="d-flex align-items-center gap-2">
                    <LocationOnIcon className="text-primary" />
                    <span className="fw-bold">Contacto y Ubicación</span>
                  </div>
                </Accordion.Header>
                <Accordion.Body className="bg-light rounded-bottom">
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold text-secondary">Teléfono de Contacto</Form.Label>
                        <InputGroup>
                          <InputGroup.Text className="bg-white">+51</InputGroup.Text>
                          <Form.Control
                            name="telefono"
                            placeholder="999888777"
                            type="tel"
                            value={empresa.telefono || ''}
                            onChange={onPhoneChange}
                            required
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold text-secondary">Google Business (Maps Link)</Form.Label>
                        <Form.Control
                          name="googleBusiness"
                          placeholder="URL de tu perfil de Google"
                          type="text"
                          value={empresa.googleBusiness || ''}
                          onChange={onChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-0">
                    <Form.Label className="fw-bold text-secondary">Dirección y Mapa</Form.Label>
                    {isLoaded ? (
                      <>
                        <AddressSearch onAddressSelect={onAddressChange} initialAddress={empresa.direccion} />
                        <GoogleMap
                          mapContainerStyle={mapContainerStyle}
                          zoom={15}
                          center={center}
                          onLoad={onMapLoad}
                          options={{ streetViewControl: false, mapTypeControl: false, styles: [
                              {
                                  "featureType": "poi",
                                  "stylers": [{ "visibility": "off" }]
                              }
                          ]}}
                        >
                          <Marker 
                            position={center} 
                            draggable={true} 
                            onDragEnd={(e) => {
                              onAddressChange({ 
                                address: empresa.direccion, 
                                lat: e.latLng.lat(), 
                                lng: e.latLng.lng() 
                              });
                            }} 
                          />
                        </GoogleMap>
                      </>
                    ) : <div className="p-3 text-center">Cargando Mapa...</div>}
                  </Form.Group>
                </Accordion.Body>
              </Accordion.Item>

              {/* SECCION 4: ADMINISTRACION */}
              <Accordion.Item eventKey="2" className="border-0 mb-0 shadow-sm rounded">
                <Accordion.Header>
                  <div className="d-flex align-items-center gap-2">
                    <InfoIcon className="text-primary" />
                    <span className="fw-bold">Información Administrativa</span>
                  </div>
                </Accordion.Header>
                <Accordion.Body className="bg-light rounded-bottom">
                  <Form.Group className="mb-0">
                    <Form.Label className="fw-bold text-secondary">Número de RUC</Form.Label>
                    <Form.Control
                      name="ruc"
                      placeholder="Número de RUC"
                      type="text"
                      value={empresa.ruc || ''}
                      onChange={onChange}
                      required
                    />
                  </Form.Group>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            {showSubmit && (
              <div className="d-grid mt-4">
                <Button
                  variant="primary"
                  type="submit"
                  size="lg"
                  className="fw-bold shadow-sm py-3 mb-2"
                  style={{ borderRadius: '15px' }}
                >
                  Guardar Cambios
                </Button>
              </div>
            )}
          </Form>
        </Card.Body>
      </Card>
      
      <style>{`
        .custom-accordion .accordion-button:not(.collapsed) {
          background-color: #e7f1ff;
          color: #0c63e4;
          box-shadow: none;
        }
        .custom-accordion .accordion-item {
          overflow: hidden;
        }
        .custom-accordion .accordion-button {
          padding: 1.25rem;
          font-size: 1.1rem;
        }
        .hover-card {
            transition: transform 0.2s ease, shadow 0.2s ease;
        }
        .hover-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
      `}</style>
    </Container>
  );
}
